import { useEffect, useReducer } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { Button, Tag } from '@/components/atoms'
import { apiFetch } from '@/lib/api'
import { useAuthStore } from '@/stores/auth-store'
import { listDrafts, resumeDraft, removeDraft, type StoredDraft } from '@/lib/draft-store'
import type { VibeProject } from '@vibe/shared-types'

/**
 * 프로젝트 선택 화면 (2026-07-20, 사용자 결정).
 *
 * 홈 '시작' → 로그인 → 이 화면. 기존에 진행하던 게 있으면 곧장 인터뷰로 직행하지 않고
 * 여기서 [이어서 할 프로젝트](진행중 draft + 완성 committed) 또는 [새 프로젝트]를 고른다.
 * 이어서 할 게 하나도 없으면 온보딩(새 프로젝트)으로 자동 이동해 이 화면을 건너뛴다.
 */

export const Route = createFileRoute('/projects')({
  component: ProjectsPage,
})

function timeAgo(ts: number): string {
  const diff = Date.now() - ts
  const min = Math.floor(diff / 60000)
  if (min < 1) return '방금'
  if (min < 60) return `${min}분 전`
  const hr = Math.floor(min / 60)
  if (hr < 24) return `${hr}시간 전`
  return `${Math.floor(hr / 24)}일 전`
}

function ProjectsPage() {
  const navigate = useNavigate()
  const idToken = useAuthStore((s) => s.idToken)
  const authStatus = useAuthStore((s) => s.status)
  const [, refresh] = useReducer((n: number) => n + 1, 0) // 로컬 draft 삭제 후 리렌더용.

  const drafts = listDrafts()

  const { data, isLoading } = useQuery({
    queryKey: ['my-projects'],
    queryFn: () => apiFetch<{ items: VibeProject[] }>('/api/projects'),
    enabled: !!idToken,
    retry: false,
  })
  const projects = data?.items ?? []

  // 이어서 할 게 아무것도 없으면(인증·목록 로딩 끝난 뒤) 온보딩으로 직행 — 선택 화면 생략.
  // 인증이 settle(ready/signedOut)되기 전엔 판단 보류 — 토큰 하이드레이션 중 성급한 리다이렉트 방지.
  const committedLoaded =
    authStatus === 'signedOut' || (authStatus === 'ready' && !isLoading)
  const nothingToResume = committedLoaded && projects.length === 0 && drafts.length === 0
  useEffect(() => {
    if (nothingToResume) navigate({ to: '/onboarding', replace: true })
  }, [nothingToResume, navigate])

  const openProject = (p: VibeProject) => {
    navigate({ to: '/ide', search: { project: p.projectId } })
  }
  const resume = (d: StoredDraft) => {
    resumeDraft(d.draftId)
    navigate({ to: '/plan', search: { seed: d.seedId } })
  }
  const discard = (d: StoredDraft) => {
    removeDraft(d.draftId)
    refresh() // 로컬 localStorage 변경 → 리렌더로 목록 갱신.
  }

  if (nothingToResume) return null

  return (
    <main className="min-h-dvh bg-stage px-6 py-12">
      <div className="mx-auto max-w-[760px]">
        <header className="mb-8">
          <h1 className="text-[24px] font-extrabold tracking-tight text-text">내 프로젝트</h1>
          <p className="mt-1 text-[14px] text-text-2">
            이어서 만들 작품을 고르거나, 새 작품을 시작해요.
          </p>
        </header>

        {drafts.length > 0 && (
          <section className="mb-8">
            <h2 className="mb-3 text-[13px] font-semibold text-text-2">진행 중</h2>
            <ul className="grid gap-3 sm:grid-cols-2">
              {drafts.map((d) => (
                <li
                  key={d.draftId}
                  className="rounded-stage border border-border bg-surface p-4 shadow-lift transition-all ease-spring"
                >
                  <div className="flex items-start gap-2">
                    <span className="text-[20px]">{d.icon ?? '📝'}</span>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[15px] font-bold text-text">{d.title}</div>
                      <div className="mt-0.5 text-[12px] text-text-3">
                        {d.cards && d.cards.length > 0 ? '계획 확인 중' : '인터뷰 중'} ·{' '}
                        {timeAgo(d.savedAt)}
                      </div>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-2">
                    <Button size="sm" onClick={() => resume(d)}>
                      이어하기
                    </Button>
                    <button
                      onClick={() => discard(d)}
                      className="ml-auto text-[12px] text-text-3 transition-colors hover:text-text"
                    >
                      삭제
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        {projects.length > 0 && (
          <section className="mb-8">
            <h2 className="mb-3 text-[13px] font-semibold text-text-2">완성한 작품</h2>
            <ul className="grid gap-3 sm:grid-cols-2">
              {projects.map((p) => (
                <li
                  key={p.projectId}
                  className="rounded-stage border border-border bg-surface p-4 shadow-lift transition-all ease-spring"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="truncate text-[15px] font-bold text-text">{p.title}</div>
                      <div className="mt-0.5 text-[12px] text-text-3">{timeAgo(p.updatedAt)}</div>
                    </div>
                    {p.deployedUrl && <Tag variant="primary">공개됨</Tag>}
                  </div>
                  <div className="mt-3">
                    <Button size="sm" variant="outline" onClick={() => openProject(p)}>
                      열기
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}

        <div className="mt-2">
          <Button size="lg" onClick={() => navigate({ to: '/onboarding' })}>
            + 새 프로젝트 시작
          </Button>
        </div>
      </div>
    </main>
  )
}
